import React, { useState, useEffect, useRef } from "react";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PlaceIcon from "@mui/icons-material/Place";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootState";
import { buildGoogleMapsReviewUrl } from "../../../utils/mapsUtils";
import "./GoogleMapsReviewCTA.scss";
import { isColorDark } from "../../../utils/colorsUtils";
import { config } from "localforage";
import { useConfig } from "../../../context/ConfigContext";

const LABELS = ["", "Décevant", "Passable", "Bien", "Très bien", "Excellent !"];

const GoogleMapsReviewCTA: React.FC = () => {
    const { config } = useConfig();
  const selectedRestaurant = useSelector(
    (state: RootState) => state.shop.selectedRestaurant
  );

  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [sent, setSent] = useState(false);
  const [animating, setAnimating] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && sent) {
        setTimeout(() => {
          setSent(false);
          setSelected(0);
          setHovered(0);
          setAnimating(false);
        }, 400);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [sent]);

  /* Tilt on mouse move */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const onMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * 8;
      const y = ((e.clientY - r.top) / r.height - 0.5) * -8;
      card.style.transform = `perspective(900px) rotateY(${x}deg) rotateX(${y}deg)`;
    };
    const onLeave = () => {
      card.style.transform =
        "perspective(900px) rotateY(0deg) rotateX(0deg)";
    };
    card.addEventListener("mousemove", onMove);
    card.addEventListener("mouseleave", onLeave);
    return () => {
      card.removeEventListener("mousemove", onMove);
      card.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  if (!selectedRestaurant) return null;

  const handleStar = (star: number) => {
    if (sent) return;
    setSelected(star);
    setAnimating(true);
    setTimeout(() => {
      setSent(true);
      setTimeout(() => {
        window.open(
          buildGoogleMapsReviewUrl(selectedRestaurant),
          "_blank",
          "noopener,noreferrer"
        );
      }, 600);
    }, 350);
  };

  const activeStar = hovered || selected;

  return (
    <div className="gmr-wrapper">
      {/* Ambient orbs */}
      <div className="gmr-orb gmr-orb--1" />
      <div className="gmr-orb gmr-orb--2" />

      <div
        ref={cardRef}
        className={`gmr-card${sent ? " gmr-card--sent" : ""}`}
        style={{ transition: "transform 0.12s ease-out" }}
      >
        {/* Shimmer overlay */}
        <div className="gmr-shimmer" />

        {/* ── Header ── */}
        <div className="gmr-header">
          <div className="gmr-badge">
            <GoogleColorIcon />
            <span>Google</span>
          </div>
          <div className="gmr-trophy">
            <EmojiEventsIcon sx={{ fontSize: 16 }} />
          </div>
        </div>

        {/* ── Headline ── */}
        <div className="gmr-headline-block">
          <h2 className="gmr-headline">
            Votre avis<span className="gmr-accent"> compte</span>
          </h2>
          {/* logo */}
          <img
            src={config?.appLogoImage || "/placeholder-logo.png"}
            alt={`${selectedRestaurant.nom} logo`}
            className="gmr-restaurant-logo"
          />
          <p className="gmr-restaurant-name">{selectedRestaurant.nom}</p>
        </div>

        {/* ── Address chip ── */}
        {selectedRestaurant.address && (
          <div className="gmr-address-chip">
            <PlaceIcon sx={{ fontSize: 12 }} />
            <span>{selectedRestaurant.address}</span>
          </div>
        )}

        {/* ── Divider ── */}
        <div className="gmr-divider" />

        {/* ── Stars or Sent state ── */}
        {!sent ? (
          <>
            <p className="gmr-instruction">
              Sélectionnez votre note&nbsp;:
            </p>
            <div className="gmr-stars" onMouseLeave={() => setHovered(0)}>
              {[1, 2, 3, 4, 5].map((s) => {
                const lit = activeStar >= s;
                return (
                  <button
                    key={s}
                    className={`gmr-star${lit ? " gmr-star--lit" : ""}${animating ? " gmr-star--animating" : ""
                      }`}
                    onMouseEnter={() => setHovered(s)}
                    onClick={() => handleStar(s)}
                    aria-label={`${s} étoile${s > 1 ? "s" : ""}`}
                    style={{ animationDelay: `${(s - 1) * 55}ms` }}
                  >
                    {lit ? (
                      <StarIcon sx={{ fontSize: 36 }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 36 }} />
                    )}
                    {/* ripple */}
                    {lit && <span className="gmr-star-ripple" />}
                  </button>
                );
              })}
            </div>
            <p
              className="gmr-label"
              style={{
                opacity: activeStar ? 1 : 0,
                transform: activeStar
                  ? "translateY(0)"
                  : "translateY(6px)",
              }}
            >
              {LABELS[activeStar]}
            </p>
          </>
        ) : (
          <div className="gmr-success">
            <CheckCircleOutlineIcon
              className="gmr-success-icon"
              sx={{ fontSize: 40 }}
            />
            <p className="gmr-success-text">Merci pour votre avis&nbsp;!</p>
            <div className="gmr-success-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <StarIcon
                  key={s}
                  sx={{
                    fontSize: 28,
                    animationDelay: `${s * 70}ms`,
                  }}
                  className={`gmr-success-star${s <= selected ? " gmr-success-star--lit" : ""
                    }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── CTA button ── */}
        <button
          className={`gmr-btn${sent ? " gmr-btn--sent" : ""}`}

          onClick={() => {
            if (sent) return;
            window.open(
              buildGoogleMapsReviewUrl(selectedRestaurant),
              "_blank",
              "noopener,noreferrer"
            );
          }}
          disabled={sent}
        >
          <span className="gmr-btn-inner">
            {sent ? (
              <>
                <span className="gmr-btn-dot" />
                <span className="gmr-btn-dot" />
                <span className="gmr-btn-dot" />
                <span style={{ marginLeft: 6 }}>
                  Ouverture de Google Maps…
                </span>
              </>
            ) : (
              <>
                <GoogleColorIconWhite />
                <span>Évaluer sur Google Maps</span>
                <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.8 }} />
              </>
            )}
          </span>
        </button>

        {/* ── Footer note ── */}
        {!sent && (
          <p className="gmr-footer">
            Vous serez redirigé vers Google Maps
          </p>
        )}
      </div>
    </div>
  );
};

/* ── SVG helpers ── */
const GoogleColorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const GoogleColorIconWhite = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="rgba(255,255,255,0.95)" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="rgba(255,255,255,0.75)" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="rgba(255,255,255,0.85)" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="rgba(255,255,255,0.95)" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default GoogleMapsReviewCTA;